import * as React from "react";
import { useState } from "react";
import { Box, createTheme, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { enUS } from "date-fns/locale";
import useTheme from "../../../../common/hooks/useTheme";

// MUI fills an empty, focused field with its own section placeholders, and
// for a text month it writes "MMMM" — so the field read "MMM DD YYYY" at rest
// and "MMMM DD YYYY" once clicked. Match the month section to the format.
const FIELD_PLACEHOLDERS = { fieldMonthPlaceholder: () => "MMM" };

type DateRangeValue = {
  startDate: Date | null;
  endDate: Date | null;
};

const CustomDateRangePicker = (props: {
  label?: string;
  value?: DateRangeValue;
  onChange?: (value: DateRangeValue) => void;
  defaultValue?: DateRangeValue;
}) => {
  const theme = useTheme();

  const [internalValue, setInternalValue] = useState<DateRangeValue>(
    props.value || props.defaultValue || { startDate: null, endDate: null }
  );
  const [focusedField, setFocusedField] = useState<"start" | "end" | null>(
    null
  );
  const [openField, setOpenField] = useState<"start" | "end" | null>(null);

  const value = props.value !== undefined ? props.value : internalValue;

  const handleStartDateChange = (newDate: Date | null) => {
    const newValue = { ...value, startDate: newDate };

    if (props.onChange) {
      props.onChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  };

  const handleEndDateChange = (newDate: Date | null) => {
    const newValue = { ...value, endDate: newDate };

    if (props.onChange) {
      props.onChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  };

  // Create theme to match TextInputField styling exactly
  const customTheme = createTheme({
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              height: "56px",
              borderRadius: "8px",
              fontFamily: "var(--font-Medium)",
              fontSize: "12px",
              lineHeight: "20px",
              // Lock the container padding to prevent icon movement
              padding: "0 12px",
              // Input text positioning with dynamic padding
              "& input": {
                padding: "16px 0",
                transition: "padding 0.2s ease-in-out",
              },
              // Dynamic input text positioning based on label state
              "&.MuiOutlinedInput-root.Mui-focused input": {
                paddingTop: "24px",
                paddingBottom: "10px",
              },
              // Keep calendar icon absolutely positioned
              "& .MuiInputAdornment-root": {
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                margin: 0,
              },
              "& fieldset": {
                borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
                top: 0,
              },
              "&:hover fieldset": {
                borderColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.colors_ds.PRIMARY_ICON_MAIN,
                borderWidth: "1px",
              },
            },
            "& .MuiInputLabel-root": {
              fontFamily: "var(--font-Medium)",
              fontSize: "10px",
              backgroundColor: "transparent",
              transition: "transform 0.2s ease-in-out",
              color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
              transform: "translate(12px, 6px) scale(1)",
              "&.Mui-focused": {
                color: theme.colors_ds.PRIMARY_ICON_MAIN,
                transform: "translate(12px, 6px) scale(1)",
              },
            },
            "& legend": {
              display: "none",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "8px",
            boxShadow: "var(--box-shadow-general)",
            marginTop: "8px",
            border: `1px solid ${theme.colors_ds.PRIMARY_ICON_MAIN}`,
          },
        },
      },
      // @ts-ignore
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
          },
          label: {
            fontFamily: "var(--font-Bold)",
            fontSize: "14px",
            color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&[aria-selected='true']": {
              backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
              color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
              fontFamily: "var(--font-Bold)",
              "&:hover": {
                backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
              },
            },
            "&:hover": {
              backgroundColor: theme.colorTokens.N[40],
            },
          },
          today: {
            borderColor: "transparent",
            '&:not([aria-selected="true"])': {
              borderColor: theme.colors_ds.PRIMARY_ICON_MAIN,
            },
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: {
            fontFamily: "var(--font-Bold)",
            fontSize: "12px",
            color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
          },
        },
      },
    },
  });

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={{ ...enUS, options: { weekStartsOn: 1 } }}
    >
      <ThemeProvider theme={customTheme}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            gap: "16px",
            alignItems: "center",
          }}
        >
          <DatePicker
            label="Start Date"
            value={value.startDate}
            onChange={handleStartDateChange}
            maxDate={value.endDate || undefined}
            format="MMM dd yyyy"
            localeText={FIELD_PLACEHOLDERS}
            open={openField === "start"}
            onOpen={() => setOpenField("start")}
            onClose={() => setOpenField(null)}
            slotProps={{
              textField: {
                fullWidth: true,
                placeholder: "MMM DD YYYY",
                onFocus: () => setFocusedField("start"),
                onBlur: () => setFocusedField(null),
                inputProps: {
                  readOnly: true,
                },
                InputLabelProps: {
                  style: {
                    transform:
                      value.startDate ||
                      focusedField === "start" ||
                      openField === "start"
                        ? "translate(12px, 8px) scale(1)"
                        : "translate(12px, 16px) scale(1.5)",
                  },
                },
                InputProps: {
                  sx: {
                    "& input": {
                      paddingTop:
                        value.startDate ||
                        focusedField === "start" ||
                        openField === "start"
                          ? "24px !important"
                          : "16px !important",
                      paddingBottom:
                        value.startDate ||
                        focusedField === "start" ||
                        openField === "start"
                          ? "10px !important"
                          : "16px !important",
                    },
                  },
                },
              },
            }}
          />
          <Box
            sx={{
              color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
              fontFamily: "var(--font-Medium)",
              fontSize: "12px",
              minWidth: "16px",
              textAlign: "center",
            }}
          >
            to
          </Box>
          <DatePicker
            label="End Date"
            value={value.endDate}
            onChange={handleEndDateChange}
            minDate={value.startDate || undefined}
            format="MMM dd yyyy"
            localeText={FIELD_PLACEHOLDERS}
            open={openField === "end"}
            onOpen={() => setOpenField("end")}
            onClose={() => setOpenField(null)}
            slotProps={{
              textField: {
                fullWidth: true,
                placeholder: "MMM DD YYYY",
                onFocus: () => setFocusedField("end"),
                onBlur: () => setFocusedField(null),
                inputProps: {
                  readOnly: true,
                },
                InputLabelProps: {
                  style: {
                    transform:
                      value.endDate ||
                      focusedField === "end" ||
                      openField === "end"
                        ? "translate(12px, 6px) scale(1)"
                        : "translate(12px, 16px) scale(1.5)",
                  },
                },
                InputProps: {
                  sx: {
                    "& input": {
                      paddingTop:
                        value.endDate ||
                        focusedField === "end" ||
                        openField === "end"
                          ? "24px !important"
                          : "16px !important",
                      paddingBottom:
                        value.endDate ||
                        focusedField === "end" ||
                        openField === "end"
                          ? "10px !important"
                          : "16px !important",
                    },
                  },
                },
              },
            }}
          />
        </Box>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default CustomDateRangePicker;
